export type MetricComparisonDirection = 'up' | 'down' | 'flat' | 'new';

export interface MetricPeriodComparison {
  label: string;
  direction: MetricComparisonDirection;
}

export interface FormatPeriodComparisonOptions {
  /** When true, a rise in the metric is bad (e.g. leads lost) — flips color and trend only; % keeps its real sign. */
  invertSentiment?: boolean;
}

function sentimentDirection(
  rawPercent: number,
  invertSentiment: boolean
): MetricComparisonDirection {
  const natural: MetricComparisonDirection =
    rawPercent > 0 ? 'up' : rawPercent < 0 ? 'down' : 'flat';
  if (!invertSentiment) return natural;
  if (natural === 'up') return 'down';
  if (natural === 'down') return 'up';
  return 'flat';
}

/**
 * Builds a footer line like "+12% from last month" or "— No change from yesterday".
 * When the prior period is zero and current is positive, shows "New" instead of a misleading %.
 */
export function formatPeriodComparison(
  current: number,
  previous: number,
  periodLabel: string,
  options?: FormatPeriodComparisonOptions
): MetricPeriodComparison {
  const period = periodLabel.toLowerCase();
  const invertSentiment = options?.invertSentiment ?? false;

  if (current === previous) {
    return {
      label: `— No change from ${period}`,
      direction: 'flat',
    };
  }

  if (previous === 0) {
    if (current > 0) {
      return {
        label: `— New vs ${period}`,
        direction: invertSentiment ? 'down' : 'new',
      };
    }
    return {
      label: `— No change from ${period}`,
      direction: 'flat',
    };
  }

  const rawPercent = Math.round(((current - previous) / previous) * 100);
  const direction = sentimentDirection(rawPercent, invertSentiment);

  if (rawPercent === 0) {
    return {
      label: `— No change from ${period}`,
      direction: 'flat',
    };
  }

  const sign = rawPercent > 0 ? '+' : '';
  return {
    label: `${sign}${rawPercent}% from ${period}`,
    direction,
  };
}
